import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Slider } from 'primereact/slider';
import { useTimer } from '../../context/TimerContext';
import axios from 'axios';
import '../../styles/Study.css';
import {auth} from "../../config/firebase";

const PomodoroTimer = ({ toast, audioRef, toggleFullScreen, isFullScreen, setIsStudyPage, isInsideStudyPage = true }) => {
    const user = auth.currentUser;
    const {
        isTimerRunning,
        setIsTimerRunning,
        timerMode,
        setTimerMode,
        timeLeft,
        setTimeLeft,
        workDuration,
        setWorkDuration,
        breakDuration,
        setBreakDuration
    } = useTimer();

    // Local state
    const [cycles, setCycles] = useState(0);
    const [tasks, setTasks] = useState([]);
    const [currentTask, setCurrentTask] = useState(null);
    const [taskName, setTaskName] = useState('');
    const [estimatedPomodoros, setEstimatedPomodoros] = useState(4);
    const [settingsVisible, setSettingsVisible] = useState(false);
    const [taskDialogVisible, setTaskDialogVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user?.uid) {
            fetchPomodoroData();
        }
    }, [user]);

    // Existing methods (fetchPomodoroData, saveCycles, etc.)
    const fetchPomodoroData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`https://api.frazza.online/pomodoro/${user.uid}`);

            if (response.data.success) {
                const data = response.data.data || {};
                const { tasks = [], cycles = 0 } = data;
                setTasks(Array.isArray(tasks) ? tasks : []);
                setCycles(typeof cycles === 'number' ? cycles : 0);
            } else {
                // Initialize with empty data if not successful
                setTasks([]);
                setCycles(0);
            }
        } catch (error) {
            console.error('Error fetching pomodoro data:', error);
            // Initialize with empty data on error
            setTasks([]);
            setCycles(0);
            
            // Only show error toast for non-404 errors
            if (error.response?.status !== 404 && toast && toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Notice',
                    detail: 'Starting with fresh pomodoro data',
                    life: 3000
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const saveCycles = async (cyclesCount) => {
        if (!user?.uid) return;

        try {
            await axios.put(`https://api.frazza.online/pomodoro/${user.uid}/cycles`, {
                cycles: cyclesCount
            });
        } catch (error) {
            console.error('Error saving cycles:', error);
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to save progress',
                    life: 3000
                });
            }
        }
    };

    useEffect(() => {
        if (setIsStudyPage && isInsideStudyPage) {
            setIsStudyPage(true);
            return () => setIsStudyPage(false);
        }
    }, [setIsStudyPage, isInsideStudyPage]);

    useEffect(() => {
        if (!isTimerRunning && timeLeft === 0) {
            if (timerMode === 'work') {
                setTimeLeft(workDuration * 60);
            } else {
                setTimeLeft(breakDuration * 60);
            }
        }
    }, [isTimerRunning, timeLeft, timerMode, workDuration, breakDuration, setTimeLeft]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (isTimerRunning && timeLeft === 0) {
            handleTimerComplete();
        }
    }, [timeLeft, isTimerRunning]);

    const handleTimerComplete = async () => {
        // All the existing timer completion logic...
        if (audioRef && audioRef.current) {
            audioRef.current.src = timerMode === 'work' ? '/sounds/work-complete.mp3' : '/sounds/break-complete.mp3';
            audioRef.current.play().catch(e => console.log('Audio playback error:', e));
        }

        if (timerMode === 'work') {
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Pomodoro Complete!',
                    detail: 'Take a short break now.',
                    life: 5000
                });
            }

            if (currentTask) {
                const updatedCompletedPomodoros = currentTask.completedPomodoros + 1;
                const updatedTasks = tasks.map(task => {
                    if (task.id === currentTask.id) {
                        return { ...task, completedPomodoros: updatedCompletedPomodoros };
                    }
                    return task;
                });

                setTasks(updatedTasks);
                const updatedTask = { ...currentTask, completedPomodoros: updatedCompletedPomodoros };
                setCurrentTask(updatedTask);

                try {
                    await axios.put(`https://api.frazza.online/pomodoro/${user.uid}/tasks/${currentTask.id}`, {
                        completedPomodoros: updatedCompletedPomodoros
                    });
                } catch (error) {
                    console.error('Error updating task:', error);
                }
            }

            const newCycles = cycles + 1;
            setCycles(newCycles);
            saveCycles(newCycles);

            setTimerMode('break');
            setTimeLeft(breakDuration * 60);
        } else {
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'info',
                    summary: 'Break Complete',
                    detail: 'Back to work!',
                    life: 5000
                });
            }
            setTimerMode('work');
            setTimeLeft(workDuration * 60);
        }
    };

    const toggleTimer = () => {
        setIsTimerRunning(!isTimerRunning);
    };

    const resetTimer = () => {
        setIsTimerRunning(false);
        setTimeLeft(timerMode === 'work' ? workDuration * 60 : breakDuration * 60);
    };

    const skipPhase = () => {
        setIsTimerRunning(false);
        if (timerMode === 'work') {
            setTimerMode('break');
            setTimeLeft(breakDuration * 60);
        } else {
            setTimerMode('work');
            setTimeLeft(workDuration * 60);
        }
    };

    const addTask = async () => {
        if (taskName.trim() && user?.uid) {
            try {
                const response = await axios.post(`https://api.frazza.online/pomodoro/${user.uid}/tasks`, {
                    name: taskName,
                    estimatedPomodoros: estimatedPomodoros || 4
                });

                if (response.data.success) {
                    const newTask = response.data.task;
                    setTasks([...tasks, newTask]);
                    setTaskName('');
                    setEstimatedPomodoros(4);
                    setTaskDialogVisible(false);

                    if (toast && toast.current) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Task Added',
                            detail: 'New task has been created',
                            life: 3000
                        });
                    }
                }
            } catch (error) {
                console.error('Error adding task:', error);
                if (toast && toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to add task',
                        life: 3000
                    });
                }
            }
        }
    };

    const deleteTask = async (taskId) => {
        try {
            await axios.delete(`https://api.frazza.online/pomodoro/${user.uid}/tasks/${taskId}`);

            const updatedTasks = tasks.filter(task => task.id !== taskId);
            setTasks(updatedTasks);

            if (currentTask?.id === taskId) {
                setCurrentTask(null);
            }

            if (toast && toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Task Deleted',
                    detail: 'Task has been removed',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            if (toast && toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to delete task',
                    life: 3000
                });
            }
        }
    };

    const selectTask = (task) => {
        setCurrentTask(task);
        if (toast && toast.current) {
            toast.current.show({
                severity: 'info',
                summary: 'Task Selected',
                detail: `Working on: ${task.name}`,
                life: 3000
            });
        }
    };

    return (
        <div className="study-method-container">
            <div className={`pomodoro-container ${isFullScreen ? 'fullscreen-layout' : 'normal-layout'}`}>
                <div className="pomodoro-timer">
                    <div className={`timer-display ${timerMode}`}>
                        <h2>{timerMode === 'work' ? 'Work Time' : 'Break Time'}</h2>
                        <div className="time">{formatTime(timeLeft)}</div>
                        <div className="cycles">Completed cycles: {cycles}</div>
                    </div>

                    <div className="timer-controls">
                        <Button
                            icon={isTimerRunning ? "pi pi-pause" : "pi pi-play"}
                            className={`p-button-rounded p-button-${isTimerRunning ? 'warning' : 'success'}`}
                            onClick={toggleTimer}
                            tooltip={isTimerRunning ? "Pause" : "Start"}
                        />
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-rounded p-button-secondary"
                            onClick={resetTimer}
                            tooltip="Reset"
                        />
                        <Button
                            icon="pi pi-step-forward"
                            className="p-button-rounded p-button-info"
                            onClick={skipPhase}
                            tooltip="Skip"
                        />
                        <Button
                            icon="pi pi-cog"
                            className="p-button-rounded p-button-help"
                            onClick={() => setSettingsVisible(true)}
                            tooltip="Settings"
                        />
                        {isFullScreen && (
                            <Button
                                icon="pi pi-desktop"
                                className="fullscreen-btn p-button-rounded p-button-outlined"
                                onClick={toggleFullScreen}
                                tooltip="Toggle Full Screen View"
                            />
                        )}
                    </div>
                </div>

                <div className="pomodoro-tasks">
                    <div className="tasks-header">
                        <h3>Tasks</h3>
                        <Button
                            icon="pi pi-plus"
                            className="p-button-rounded p-button-sm p-button-outlined"
                            onClick={() => setTaskDialogVisible(true)}
                            tooltip="Add Task"
                        />
                    </div>

                    <div className="tasks-list">
                        {loading ? (
                            <div className="loading-tasks">Loading tasks...</div>
                        ) : tasks.length === 0 ? (
                            <div className="no-tasks">No tasks added yet. Add a task to track your pomodoros.</div>
                        ) : (
                            tasks.map(task => (
                                <div
                                    key={task.id}
                                    className={`task-item ${currentTask?.id === task.id ? 'active' : ''}`}
                                    onClick={() => selectTask(task)}
                                >
                                    <div className="task-name">{task.name}</div>
                                    <div className="task-pomodoros">
                                        {task.completedPomodoros} / {task.estimatedPomodoros} pomodoros
                                    </div>
                                    <Button
                                        icon="pi pi-trash"
                                        className="p-button-rounded p-button-text p-button-danger task-delete-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteTask(task.id);
                                        }}
                                        tooltip="Delete Task"
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Settings Dialog with unique classname */}
            <Dialog
                header="Pomodoro Settings"
                visible={settingsVisible}
                className="pomo-settings-dialog frazza-dialog-sm"
                onHide={() => setSettingsVisible(false)}
                modal
                dismissableMask={true}
                closable={true}
                footer={
                    <Button
                        label="Apply"
                        icon="pi pi-check"
                        onClick={() => {
                            resetTimer();
                            setSettingsVisible(false);
                        }}
                    />
                }
            >
                <div className="pomo-settings-container">
                    <div className="frazza-form-group">
                        <label className="frazza-form-label">Work Duration: {workDuration} minutes</label>
                        <Slider
                            value={workDuration}
                            onChange={(e) => setWorkDuration(e.value)}
                            min={5}
                            max={60}
                            step={5}
                            className="pomo-settings-slider"
                        />
                    </div>

                    <div className="frazza-form-group">
                        <label className="frazza-form-label">Break Duration: {breakDuration} minutes</label>
                        <Slider
                            value={breakDuration}
                            onChange={(e) => setBreakDuration(e.value)}
                            min={1}
                            max={30}
                            step={1}
                            className="pomo-settings-slider"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Task Dialog with unique classname */}
            <Dialog
                header="Add Task"
                visible={taskDialogVisible}
                className="pomo-task-dialog frazza-dialog-sm"
                onHide={() => setTaskDialogVisible(false)}
                modal
                dismissableMask={true}
                closable={true}
                footer={(
                    <>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={() => setTaskDialogVisible(false)}
                        />
                        <Button
                            label="Add Task"
                            icon="pi pi-check"
                            onClick={addTask}
                        />
                    </>
                )}
            >
                <div className="pomo-task-dialog-content">
                    <div className="frazza-form-group">
                        <label htmlFor="taskName" className="frazza-form-label required">Task Name</label>
                        <InputText
                            id="taskName"
                            value={taskName}
                            onChange={(e) => setTaskName(e.target.value)}
                            className="frazza-input"
                            autoFocus
                            placeholder="What are you working on?"
                        />
                    </div>
                    <div className="frazza-form-group">
                        <label htmlFor="estimatedPomodoros" className="frazza-form-label">Estimated Pomodoros: {estimatedPomodoros}</label>
                        <Slider
                            id="estimatedPomodoros"
                            value={estimatedPomodoros}
                            onChange={(e) => setEstimatedPomodoros(e.value)}
                            min={1}
                            max={10}
                            className="pomo-task-slider"
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default PomodoroTimer;